import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, ChevronDownIcon, SearchIcon, UserIcon, XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { Truncate } from '@/components/ui/truncate';
import { useOrganizationUsers } from '@/hooks/use-organization-users';
import type { IUser } from '@/features/auth/interfaces';
import { getUserDisplayName, getUserInitials } from './user-display';

export interface UserSelectProps {
    /** Selected user id ('' = none). */
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    /** Extra classes for the portaled popover content (e.g. a z-index override
     *  when the trigger lives inside a high-z fixed container like the call widget). */
    popoverClassName?: string;
}

function UserRow({ user }: { user: IUser }) {
    const name = getUserDisplayName(user);
    return (
        <>
            <Avatar size="sm">
                {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={name} />}
                <AvatarFallback className="text-[10px]">{getUserInitials(user)}</AvatarFallback>
            </Avatar>
            {/* `revealOn="hover"`: the row's click belongs to selection. The tooltip
                appears only when the text is actually cut. */}
            <Truncate
                text={name}
                truncateMode="css"
                revealOn="hover"
                className="flex-1 text-left text-text-primary"
            />
            {/* Full email whenever it fits; only cut past ~60% of the row, so the
                name always keeps room. */}
            <Truncate
                text={user.email}
                truncateMode="css"
                revealOn="hover"
                className="max-w-[60%] shrink-0 text-xs text-text-tertiary"
            />
        </>
    );
}

/**
 * The API defaults to 20 members per page and this picker searches client-side,
 * so without an explicit limit anyone past the 20th member could never be found
 * or picked (and an existing assignee among them rendered as a raw id). 100 is
 * the API's max page size.
 */
const USER_PICKER_PARAMS = { limit: 100 } as const;

/**
 * Single-select org-member picker for record USER fields. Client-side search
 * over the shared `useOrganizationUsers` page; degrades to a disabled input
 * showing the raw id when the members query errors (403).
 */
export function UserSelect({ value, onChange, disabled, popoverClassName }: UserSelectProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const usersQuery = useOrganizationUsers(USER_PICKER_PARAMS);

    const users = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data]);
    const filteredUsers = useMemo(() => {
        if (!search) return users;
        const query = search.toLowerCase();
        return users.filter(
            (user) =>
                getUserDisplayName(user).toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query),
        );
    }, [search, users]);

    if (usersQuery.isError) {
        return (
            <Input
                value={value}
                disabled
                readOnly
                aria-label={t('recordForm.userUnavailable', 'User picker unavailable')}
            />
        );
    }

    const selectedUser = users.find((user) => user.id === value);
    const selectedLabel = selectedUser
        ? getUserDisplayName(selectedUser)
        : value
          ? `${value.slice(0, 8)}…`
          : '';

    const handleSelect = (userId: string) => {
        onChange(userId);
        setOpen(false);
        setSearch('');
    };

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setSearch('');
            }}
            modal
        >
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className="h-9 w-full justify-between gap-2 bg-bg-surface px-3 font-normal"
                >
                    {value ? (
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                            {selectedUser && (
                                <Avatar size="sm">
                                    {selectedUser.avatarUrl && (
                                        <AvatarImage
                                            src={selectedUser.avatarUrl}
                                            alt={selectedLabel}
                                        />
                                    )}
                                    <AvatarFallback className="text-[10px]">
                                        {getUserInitials(selectedUser)}
                                    </AvatarFallback>
                                </Avatar>
                            )}
                            <span className="truncate text-text-primary">{selectedLabel}</span>
                        </span>
                    ) : (
                        <span className="flex min-w-0 flex-1 items-center gap-2 text-text-tertiary">
                            <UserIcon className="size-4 shrink-0" />
                            <span className="truncate">
                                {t('recordForm.userPlaceholder', 'Select a user')}
                            </span>
                        </span>
                    )}
                    <span className="flex shrink-0 items-center gap-1">
                        {value && !disabled && (
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label={t('recordForm.clearSelection', 'Clear selection')}
                                className="rounded-sm text-text-tertiary hover:text-text-primary"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onChange('');
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onChange('');
                                    }
                                }}
                            >
                                <XIcon className="size-4" />
                            </span>
                        )}
                        <ChevronDownIcon
                            className={cn(
                                'size-4 text-text-tertiary transition-transform',
                                open && 'rotate-180',
                            )}
                            aria-hidden="true"
                        />
                    </span>
                </Button>
            </PopoverTrigger>

            <PopoverContent
                className={cn(
                    'w-(--radix-popover-trigger-width) min-w-64 gap-0 p-0',
                    popoverClassName,
                )}
                align="start"
                sideOffset={6}
            >
                <div className="border-b border-border-default px-1">
                    <Input
                        startIcon={<SearchIcon />}
                        placeholder={t('recordForm.searchUsers', 'Search users…')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        className="h-8 rounded-none border-0 shadow-none focus-visible:shadow-none focus-visible:border-0"
                        data-size="sm"
                    />
                </div>
                <div className="max-h-60 overflow-y-auto overscroll-contain p-1">
                    {usersQuery.isLoading ? (
                        <div className="flex justify-center py-4">
                            <Spinner />
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="py-4 text-center text-sm text-text-tertiary">
                            {t('recordForm.noUsers', 'No users found.')}
                        </div>
                    ) : (
                        filteredUsers.map((user) => {
                            const isSelected = user.id === value;
                            return (
                                <Button
                                    key={user.id}
                                    type="button"
                                    variant="ghost"
                                    onClick={() => handleSelect(user.id)}
                                    className={cn(
                                        'flex h-auto w-full cursor-default items-center justify-start gap-2.5 rounded-sm px-2 py-2 text-sm font-normal',
                                        isSelected &&
                                            'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300',
                                    )}
                                >
                                    <UserRow user={user} />
                                    {isSelected && (
                                        <CheckIcon className="size-4 shrink-0 text-primary-500" />
                                    )}
                                </Button>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
